import React, { createContext, useState, useCallback, useContext } from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

const ALERT_OVERLAY_Z = 12000;

const AlertLoadingContext = createContext({
  showAlert: (msg, severity) => {},
  closeAlert: () => {},
  setAlertLoading: (loading, message) => {},
  alertLoading: false,
});

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export function AlertLoadingProvider({ children }) {
  // Alert state
  const [open, setOpen] = useState(false);
  const [alertProps, setAlertProps] = useState({ message: '', severity: 'info', persist: false });
  
  // Loading state
  const [alertLoading, setAlertLoadingState] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  const showAlert = useCallback((message = "", severity = 'info', persist = false) => {
    setAlertProps({ message, severity, persist });
    setOpen(true);
  }, []);

  const setAlertLoading = useCallback((loading = false, message = 'Loading...') => {
    setAlertLoadingState(loading);
    setLoadingMessage(message);
  }, []);

  const closeAlert = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  return (
    <AlertLoadingContext.Provider value={{ showAlert, closeAlert, setAlertLoading, alertLoading }}>
      {children}
      
      {/* Alert Snackbar */}
      <Snackbar
        open={open}
        autoHideDuration={alertProps.persist ? null : 3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: ALERT_OVERLAY_Z }}
      >
        <Alert onClose={handleClose} severity={alertProps.severity} sx={{ width: '100%' }}>
          {alertProps.message}
        </Alert>
      </Snackbar>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: ALERT_OVERLAY_Z,
          flexDirection: 'column',
          gap: 2
        }}
        open={alertLoading}
      >
        <CircularProgress color="inherit" size={60} />
        {loadingMessage && (
          <Typography variant="h6" component="div" sx={{ mt: 2 }}>
            {loadingMessage}
          </Typography>
        )}
      </Backdrop>
    </AlertLoadingContext.Provider>
  );
}

/**
 * Custom hook to access alert and loading functions from context
 */
export function useAlert() {
  const context = useContext(AlertLoadingContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertLoadingProvider');
  }
  return context.showAlert;
}

/**
 * Custom hook to access alert functions (show and close) from context
 */
export function useAlertActions() {
  const context = useContext(AlertLoadingContext);
  if (!context) {
    throw new Error('useAlertActions must be used within an AlertLoadingProvider');
  }
  return { showAlert: context.showAlert, closeAlert: context.closeAlert };
}

/**
 * Custom hook to access loading functions from context
 */
export function useLoading() {
  const context = useContext(AlertLoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within an AlertLoadingProvider');
  }
  return { setAlertLoading: context.setAlertLoading, alertLoading: context.alertLoading };
}

/**
 * Custom hook to access both alert and loading functions
 */
export function useAlertLoading() {
  const context = useContext(AlertLoadingContext);
  if (!context) {
    throw new Error('useAlertLoading must be used within an AlertLoadingProvider');
  }
  return {
    showAlert: context.showAlert,
    setAlertLoading: context.setAlertLoading,
    alertLoading: context.alertLoading
  };
}